import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./CompetitorsSection.module.scss";
import { useState } from "react";
import { TrashIcon } from "@/frontend/shared/assets/icons";
import { CompetitorPlaceholderIcon } from "../../../../../shared/assets/icons/CompetitorPlaceholder";
import { Input } from "@/frontend/shared/ui/Input";
import { UploadLogo } from "@/frontend/shared/ui/UploadLogo/UploadLogo";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { fakeSearchResults } from "./data";
import clsx from "clsx";

type TCompetitor = {
  name: string;
  description: string;
  url: string;
  image: string;
  linkedInUrl: string;
};

export function CompetitorsSection() {
  const [search, setSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [formVisible, setFormVisible] = useState(false);
  const [isHoveringOnSearch, setIsHoveringOnSearch] = useState(false);

  const [searchResults, setSearchResults] = useState<TCompetitor[]>(fakeSearchResults);

  const [competitors, setCompetitors] = useState<TCompetitor[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    url: "",
    image: "",
    description: "",
    linkedInUrl: ""
  });

  function handleAddCompetitor() {
    setCompetitors([...competitors, { ...formData }]);
    setFormData({
      name: "",
      url: "",
      image: "",
      description: "",
      linkedInUrl: ""
    });
    setFormVisible(false);
  }

  return (
    <div className={styles.container}>
      <Typography variant="heading-6" color="greyscale900">
        Competitors
      </Typography>

      <div className={styles.addCompetitorContainer}>
        <Input
          placeholder="Search for a competitor"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setIsSearching(true)}
          onBlur={() => {
            if (isHoveringOnSearch == false) {
              setIsSearching(false);
            }
          }}
        />
        <div
          className={clsx(styles.searchResults, { [styles.show]: isSearching })}
          onMouseEnter={() => setIsHoveringOnSearch(true)}
          onMouseLeave={() => setIsHoveringOnSearch(false)}
        >
          <div className={styles.searchResCont}>
            {searchResults.map((competitor, index) => (
              <div
                key={index}
                className={styles.searchResult}
                onClick={() => {
                  setCompetitors([...competitors, competitor]);
                  setIsSearching(false);
                }}
              >
                <div className={styles.imageContainer}>
                  {competitor.image ? (
                    <img src={competitor.image} alt="logo" className={styles.searchImage} />
                  ) : (
                    <div className={styles.searchPlaceholderIconContainer}>
                      <CompetitorPlaceholderIcon />
                    </div>
                  )}
                </div>
                <div className={styles.competitorCardText}>
                  <Typography variant="label-4" color="greyscale900">
                    {competitor.name}
                  </Typography>
                  <Typography variant="body-3" color="greyscale600">
                    {competitor.description}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
          <div className={styles.cantFindContainer}>
            <Typography variant="body-3" color="greyscale700">
              Can't find the competitor you are looking for?
            </Typography>
            <NormalButton
              variant="secondary"
              size="small"
              color="greyscale900"
              onClick={() => {
                setFormVisible(true);
                setIsSearching(false);
              }}
            >
              Add Competitor
            </NormalButton>
          </div>
        </div>
        <div></div>
      </div>

      <div className={styles.competitorsList}>
        <Typography color="greyscale700" variant="label-4">
          List of competitors
        </Typography>
        {competitors.map((competitor, index) => (
          <div key={index} className={styles.competitor}>
            <div className={styles.competitorInfo}>
              <div className={styles.imageContainer}>
                {competitor.image ? (
                  <img src={competitor.image} alt="logo" className={styles.competitorImage} />
                ) : (
                  <div className={styles.placeholderIconContainer}>
                    <CompetitorPlaceholderIcon />
                  </div>
                )}
              </div>

              <div className={styles.competitorCardText}>
                <div className={styles.SomeOtherContainer}>
                  <div>
                    <Typography variant="heading-7" color="greyscale900">
                      {competitor.name}
                    </Typography>
                    <a href={competitor.url} target="_blank" rel="noreferrer">
                      <Typography variant="body-2" color="greyscale600">
                        {competitor.url}
                      </Typography>
                    </a>
                  </div>
                  <div className={styles.deleteCompetitorIcon}>
                    <TrashIcon onClick={() => setCompetitors(competitors.filter((_, i) => i !== index))} />
                  </div>
                </div>
                <Typography variant="body-2" color="greyscale600">
                  {competitor.description}
                </Typography>
              </div>
            </div>
          </div>
        ))}

        <div className={clsx(styles.wholeForm, { [styles.show]: formVisible })}>
          <div className={styles.formContainer}>
            <UploadLogo
              image={formData.image}
              onRemove={() => setFormData((prev) => ({ ...prev, image: "" }))}
              onUpload={(e) => setFormData((prev) => ({ ...prev, image: e }))}
              text="Upload Image"
            />
            <div className={styles.inputContainer}>
              <Typography variant="label-4">Name</Typography>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Company name"
              />
            </div>
            <div className={styles.inputContainer}>
              <Typography variant="label-4">Website</Typography>
              <Input
                value={formData.url}
                onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
                placeholder="Website"
              />
            </div>
            <div className={styles.inputContainer}>
              <Typography variant="label-4">Description</Typography>
              <Input
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Description"
              />
            </div>
            <div className={styles.inputContainer}>
              <Typography variant="label-4">LinkedIn URL</Typography>
              <Input
                value={formData.linkedInUrl}
                onChange={(e) => setFormData((prev) => ({ ...prev, linkedInUrl: e.target.value }))}
                placeholder="LinkedIn Url"
              />
            </div>
            <NormalButton size="medium" onClick={handleAddCompetitor}>
              Add Competitor
            </NormalButton>
            <NormalButton size="medium" variant="tertiary" color="greyscale600" onClick={() => setFormVisible(false)}>
              Cancel
            </NormalButton>
          </div>
        </div>
      </div>

      <div className={styles.bottomButtonsContainer}>
        <NormalButton size="large">Proceed</NormalButton>
        <NormalButton size="large" variant="secondary" color="greyscale800">
          Step back
        </NormalButton>
      </div>
    </div>
  );
}
